/**
 * TanStack Query hooks for the Working-Layer (workspaces) API.
 *
 * Workspaces are the mutable working-document surface: staff upload drafts,
 * iterate versions, and promote a final version into the immutable Vault.
 * Routes live at /api/workspaces/* (prefixed by apiFetch's `/api` base).
 *
 * Design rationale: mirrors documents.ts idioms exactly — same apiFetch
 * signature, same query-key object pattern, same invalidation style.
 *
 * ADR context: surface-choice A (Documents page = Working ↔ Vault toggle).
 * SharePoint co-edit (ADR-0077): create with `storageLocation: "sharepoint"`,
 * multipart upload via `/sharepoint-upload`, open via `/edit-url`.
 *
 * Note on Zod schemas: @l30/shared is built with Zod v3 while packages/web
 * uses Zod v4. Mixing the two inside a single z.object() wrapper causes a
 * type error. Response types are instead carried as explicit TypeScript
 * generics on apiFetch<T>() — the same pattern used by hooks in documents.ts
 * that omit the optional responseSchema arg (e.g. useDocumentDownloadUrl).
 */

import { useMutation, useQuery, useQueries, useQueryClient } from "@tanstack/react-query";

import { apiFetchUnchecked } from "./client.js";

import type {
  Workspace,
  WorkspaceCollaborationSite,
  WorkspaceDocument,
  WorkspaceStorageLocation,
} from "@l30/shared";

// ─── Local folder type (no response schema in @l30/shared yet; keep loose) ──
export interface WorkspaceFolder {
  id: string;
  workspaceId: string;
  parentId: string | null;
  name: string;
  sectionKey: string | null;
  sortOrder: number;
}

/** Office Open XML types that benefit from SharePoint co-authoring. */
const OFFICE_COEDIT_EXTENSIONS = new Set([".docx", ".xlsx", ".pptx", ".doc", ".xls", ".ppt"]);

export function prefersSharePointCoEdit(filename: string): boolean {
  const dot = filename.lastIndexOf(".");
  if (dot < 0) return false;
  return OFFICE_COEDIT_EXTENSIONS.has(filename.slice(dot).toLowerCase());
}

// ─── Query keys ────────────────────────────────────────────────────────────────

/**
 * Centralised query-key factory.  The structure mirrors documents.ts
 * (flat const arrays keyed by entity + id) so cache invalidation is
 * predictable across features.
 */
function tenantKey(tenantSlug?: string | null): string {
  return tenantSlug && tenantSlug.length > 0 ? tenantSlug : "default";
}

export const workspaceKeys = {
  /** All workspaces list (optionally scoped to a partner via X-Tenant-ID). */
  list: (tenantSlug?: string | null) => ["workspaces", tenantKey(tenantSlug)] as const,
  /** Single workspace detail */
  detail: (id: string, tenantSlug?: string | null) =>
    ["workspaces", tenantKey(tenantSlug), id] as const,
  /** Folders within a workspace */
  folders: (id: string, tenantSlug?: string | null) =>
    ["workspaces", tenantKey(tenantSlug), id, "folders"] as const,
  /** Documents within a workspace */
  documents: (id: string, tenantSlug?: string | null, archivedOnly?: boolean) =>
    [
      "workspaces",
      tenantKey(tenantSlug),
      id,
      "documents",
      archivedOnly ? "archived" : "active",
    ] as const,
  /** Collaboration sites attached to a workspace */
  collaborationSites: (id: string, tenantSlug?: string | null) =>
    ["workspaces", tenantKey(tenantSlug), id, "collaboration-sites"] as const,
  /** SharePoint path configured? */
  sharepointConfig: (tenantSlug?: string | null) =>
    ["workspaces", tenantKey(tenantSlug), "sharepoint-config"] as const,
};

// ─── Workspace queries ──────────────────────────────────────────────────────

/** List all workspaces visible to the current user. */
export function useWorkspaces(options?: { enabled?: boolean; tenantSlug?: string | null }) {
  // `undefined` = host default tenant; `null` = partner not chosen yet (skip).
  const tenantSlug = options?.tenantSlug;
  return useQuery({
    queryKey: workspaceKeys.list(tenantSlug),
    queryFn: () =>
      apiFetchUnchecked<{ workspaces: Workspace[] }>("/workspaces", {
        tenantOverride: typeof tenantSlug === "string" ? tenantSlug : undefined,
      }),
    enabled: (options?.enabled ?? true) && tenantSlug !== null,
  });
}

/**
 * Create a workspace. ADMIN/EXEC only on the API.
 * Pass `ownerEmail` to make a personal vault (ADR-0106); omit for a shared workspace.
 */
export function useCreateWorkspace(options?: { tenantSlug?: string | null }) {
  const qc = useQueryClient();
  const tenantSlug = options?.tenantSlug ?? null;
  return useMutation({
    mutationFn: (args: {
      name: string;
      kind?: string;
      visibleToRoles?: string[];
      ownerEmail?: string | null;
    }) =>
      apiFetchUnchecked<Workspace>("/workspaces", {
        method: "POST",
        tenantOverride: tenantSlug ?? undefined,
        body: JSON.stringify({
          name: args.name,
          kind: args.kind ?? "general",
          visibleToRoles: args.ownerEmail ? [] : (args.visibleToRoles ?? []),
          ownerEmail: args.ownerEmail?.trim() ? args.ownerEmail.trim() : null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workspaceKeys.list(tenantSlug) });
      if (tenantSlug) {
        void qc.invalidateQueries({ queryKey: ["partner-vaults", "workspaces", tenantSlug] });
      }
    },
  });
}

/** Whether SHAREPOINT_SITE_PATH is set on the API (co-edit create path enabled). */
export function useSharePointConfig(options?: { tenantSlug?: string | null }) {
  const tenantSlug = options?.tenantSlug ?? null;
  return useQuery({
    queryKey: workspaceKeys.sharepointConfig(tenantSlug),
    queryFn: () =>
      apiFetchUnchecked<{ enabled: boolean }>("/workspaces/sharepoint-config", {
        tenantOverride: tenantSlug ?? undefined,
      }),
    staleTime: 60_000,
  });
}

/** List folders (section subfolders) within a workspace. */
export function useWorkspaceFolders(workspaceId: string, options?: { tenantSlug?: string | null }) {
  const tenantSlug = options?.tenantSlug ?? null;
  return useQuery({
    queryKey: workspaceKeys.folders(workspaceId, tenantSlug),
    queryFn: () =>
      apiFetchUnchecked<{ folders: WorkspaceFolder[] }>(`/workspaces/${workspaceId}/folders`, {
        tenantOverride: tenantSlug ?? undefined,
      }),
    enabled: !!workspaceId,
  });
}

/** List documents in a workspace. Skips the query when workspaceId is empty. */
export function useWorkspaceDocuments(
  workspaceId: string,
  options?: { tenantSlug?: string | null; archivedOnly?: boolean },
) {
  const tenantSlug = options?.tenantSlug ?? null;
  const archivedOnly = options?.archivedOnly === true;
  return useQuery({
    queryKey: workspaceKeys.documents(workspaceId, tenantSlug, archivedOnly),
    queryFn: () => {
      const params = new URLSearchParams();
      if (archivedOnly) params.set("archivedOnly", "true");
      const qs = params.toString();
      return apiFetchUnchecked<{ documents: WorkspaceDocument[] }>(
        `/workspaces/${workspaceId}/documents${qs ? `?${qs}` : ""}`,
        { tenantOverride: tenantSlug ?? undefined },
      );
    },
    enabled: !!workspaceId,
  });
}

/** Bounded index for Link Object / Insights search — not an unbounded crawl. */
export function useWorkspaceDocumentsIndex(enabled: boolean) {
  const list = useWorkspaces({ enabled });
  const workspaceIds = (list.data?.workspaces ?? []).slice(0, 8).map((row) => row.id);
  const queries = useQueries({
    queries: workspaceIds.map((workspaceId) => ({
      queryKey: workspaceKeys.documents(workspaceId, null, false),
      queryFn: () =>
        apiFetchUnchecked<{ documents: WorkspaceDocument[] }>(
          `/workspaces/${workspaceId}/documents`,
        ),
      enabled,
    })),
  });
  return {
    documents: queries.flatMap((query, index) => {
      const workspaceId = workspaceIds[index];
      if (!workspaceId) return [];
      return (query.data?.documents ?? [])
        .filter((doc) => !doc.archivedAt)
        .map((doc) => ({ id: doc.id, workspaceId, title: doc.title }));
    }),
    isFetching: list.isFetching || queries.some((query) => query.isFetching),
  };
}

export function useWorkspaceCollaborationSites(
  workspaceId: string,
  options?: { tenantSlug?: string | null },
) {
  const tenantSlug = options?.tenantSlug ?? null;
  return useQuery({
    queryKey: workspaceKeys.collaborationSites(workspaceId, tenantSlug),
    queryFn: () =>
      apiFetchUnchecked<{ sites: WorkspaceCollaborationSite[] }>(
        `/workspaces/${workspaceId}/collaboration-sites`,
        { tenantOverride: tenantSlug ?? undefined },
      ),
    enabled: !!workspaceId,
  });
}

export function useCreateWorkspaceCollaborationSite(
  workspaceId: string,
  options?: { tenantSlug?: string | null },
) {
  const qc = useQueryClient();
  const tenantSlug = options?.tenantSlug ?? null;
  return useMutation({
    mutationFn: (args: { name: string; sitePath: string; isDefault?: boolean }) =>
      apiFetchUnchecked<WorkspaceCollaborationSite>(
        `/workspaces/${workspaceId}/collaboration-sites`,
        {
          method: "POST",
          tenantOverride: tenantSlug ?? undefined,
          body: JSON.stringify(args),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: workspaceKeys.collaborationSites(workspaceId, tenantSlug),
      });
    },
  });
}

export function useDeleteWorkspaceCollaborationSite(
  workspaceId: string,
  options?: { tenantSlug?: string | null },
) {
  const qc = useQueryClient();
  const tenantSlug = options?.tenantSlug ?? null;
  return useMutation({
    mutationFn: (siteId: string) =>
      apiFetchUnchecked<void>(`/workspaces/${workspaceId}/collaboration-sites/${siteId}`, {
        method: "DELETE",
        tenantOverride: tenantSlug ?? undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: workspaceKeys.collaborationSites(workspaceId, tenantSlug),
      });
    },
  });
}

/**
 * Create a new workspace document record (step 0 of the upload).
 * After creation the caller uploads a version via useUploadWorkspaceVersion
 * (blob) or useUploadSharePointVersion (sharepoint).
 */
export function useCreateWorkspaceDocument(options?: { tenantSlug?: string | null }) {
  const qc = useQueryClient();
  const tenantSlug = options?.tenantSlug ?? null;
  return useMutation({
    mutationFn: (args: {
      workspaceId: string;
      title: string;
      folderId?: string | null;
      storageLocation?: WorkspaceStorageLocation;
      collaborationSiteId?: string | null;
    }) =>
      apiFetchUnchecked<WorkspaceDocument>(`/workspaces/${args.workspaceId}/documents`, {
        method: "POST",
        tenantOverride: tenantSlug ?? undefined,
        body: JSON.stringify({
          workspaceId: args.workspaceId,
          title: args.title,
          folderId: args.folderId ?? null,
          storageLocation: args.storageLocation ?? "blob",
          collaborationSiteId: args.collaborationSiteId ?? null,
        }),
      }),
    onSuccess: (_data, args) => {
      qc.invalidateQueries({
        queryKey: workspaceKeys.documents(args.workspaceId, tenantSlug, false),
      });
      qc.invalidateQueries({
        queryKey: workspaceKeys.documents(args.workspaceId, tenantSlug, true),
      });
    },
  });
}

/**
 * Resolve the editor URL for a document's current version (signed blob download
 * or SharePoint Office-for-the-web webUrl). Opens in a new tab via the caller.
 */
export function useWorkspaceDocumentEditUrl(options?: { tenantSlug?: string | null }) {
  const tenantSlug = options?.tenantSlug ?? null;
  return useMutation({
    mutationFn: (documentId: string) =>
      apiFetchUnchecked<{ url: string; storageLocation: string }>(
        `/workspaces/documents/${documentId}/edit-url`,
        { tenantOverride: tenantSlug ?? undefined },
      ),
  });
}

// ─── Version upload mutation (3-step: token → PUT → register) ──────────────

/**
 * Upload a new version of a blob-located workspace document.
 *
 * Three-step flow mirrors vault upload-document-modal:
 *  1. POST /workspaces/documents/:documentId/upload-token  → { uploadUrl, blobKey }
 *  2. PUT <uploadUrl>  (direct to Azure Blob / R2 — no auth header)
 *  3. POST /workspaces/documents/:documentId/versions  → registered version
 */
export function useUploadWorkspaceVersion(options?: { tenantSlug?: string | null }) {
  const qc = useQueryClient();
  const tenantSlug = options?.tenantSlug ?? null;
  return useMutation({
    mutationFn: async (args: {
      workspaceId: string;
      documentId: string;
      file: File;
      note?: string;
    }) => {
      const token = await apiFetchUnchecked<{ uploadUrl: string; blobKey: string }>(
        `/workspaces/documents/${args.documentId}/upload-token`,
        {
          method: "POST",
          tenantOverride: tenantSlug ?? undefined,
          body: JSON.stringify({
            filename: args.file.name,
            contentType: args.file.type || "application/octet-stream",
          }),
        },
      );

      const putRes = await fetch(token.uploadUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": args.file.type || "application/octet-stream",
        },
        body: args.file,
      });
      if (!putRes.ok) {
        throw new Error(`Blob PUT failed: ${putRes.status} ${putRes.statusText}`);
      }

      return apiFetchUnchecked(`/workspaces/documents/${args.documentId}/versions`, {
        method: "POST",
        tenantOverride: tenantSlug ?? undefined,
        body: JSON.stringify({
          filename: args.file.name,
          mimeType: args.file.type || "application/octet-stream",
          sizeBytes: args.file.size,
          blobKey: token.blobKey,
          note: args.note,
        }),
      });
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({
        queryKey: workspaceKeys.documents(args.workspaceId, tenantSlug, false),
      });
      qc.invalidateQueries({
        queryKey: workspaceKeys.documents(args.workspaceId, tenantSlug, true),
      });
      qc.invalidateQueries({ queryKey: workspaceKeys.list(tenantSlug) });
    },
  });
}

/**
 * Upload a new version of a SharePoint-located workspace document.
 * Multipart POST through the API (Graph upload is server-side — ADR-0077).
 */
export function useUploadSharePointVersion(options?: { tenantSlug?: string | null }) {
  const qc = useQueryClient();
  const tenantSlug = options?.tenantSlug ?? null;
  return useMutation({
    mutationFn: async (args: { workspaceId: string; documentId: string; file: File }) => {
      const form = new FormData();
      form.append("file", args.file);
      return apiFetchUnchecked<{ id: string; version: number }>(
        `/workspaces/documents/${args.documentId}/sharepoint-upload`,
        {
          method: "POST",
          tenantOverride: tenantSlug ?? undefined,
          body: form,
        },
      );
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({
        queryKey: workspaceKeys.documents(args.workspaceId, tenantSlug, false),
      });
      qc.invalidateQueries({
        queryKey: workspaceKeys.documents(args.workspaceId, tenantSlug, true),
      });
      qc.invalidateQueries({ queryKey: workspaceKeys.list(tenantSlug) });
    },
  });
}

export function useArchiveWorkspaceDocument(options?: { tenantSlug?: string | null }) {
  const qc = useQueryClient();
  const tenantSlug = options?.tenantSlug ?? null;
  return useMutation({
    mutationFn: (args: { workspaceId: string; documentId: string }) =>
      apiFetchUnchecked<WorkspaceDocument>(`/workspaces/documents/${args.documentId}/archive`, {
        method: "POST",
        tenantOverride: tenantSlug ?? undefined,
      }),
    onSuccess: (_data, args) => {
      qc.invalidateQueries({
        queryKey: workspaceKeys.documents(args.workspaceId, tenantSlug, false),
      });
      qc.invalidateQueries({
        queryKey: workspaceKeys.documents(args.workspaceId, tenantSlug, true),
      });
    },
  });
}

export function useUnarchiveWorkspaceDocument(options?: { tenantSlug?: string | null }) {
  const qc = useQueryClient();
  const tenantSlug = options?.tenantSlug ?? null;
  return useMutation({
    mutationFn: (args: { workspaceId: string; documentId: string }) =>
      apiFetchUnchecked<WorkspaceDocument>(`/workspaces/documents/${args.documentId}/unarchive`, {
        method: "POST",
        tenantOverride: tenantSlug ?? undefined,
      }),
    onSuccess: (_data, args) => {
      qc.invalidateQueries({
        queryKey: workspaceKeys.documents(args.workspaceId, tenantSlug, false),
      });
      qc.invalidateQueries({
        queryKey: workspaceKeys.documents(args.workspaceId, tenantSlug, true),
      });
    },
  });
}

// ─── Promote to Vault ───────────────────────────────────────────────────────

/**
 * Promote a workspace document's current version into the immutable Vault.
 * After success, invalidates both the workspace documents list and the
 * vault-documents list so both surfaces reflect the new vault entry.
 */
export function usePromoteWorkspaceDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      documentId: string;
      vaultFolderId: string;
      renderAsPdf?: boolean;
      applySignatureFields?: boolean;
    }) =>
      apiFetchUnchecked<{
        vaultDocumentId: string;
        documentId: string;
        renderedAsPdf?: boolean;
        templateFieldCount?: number;
      }>(`/workspaces/documents/${args.documentId}/promote`, {
        method: "POST",
        body: JSON.stringify({
          vaultFolderId: args.vaultFolderId,
          renderAsPdf: args.renderAsPdf,
          applySignatureFields: args.applySignatureFields,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      qc.invalidateQueries({ queryKey: ["vault-documents"] });
    },
  });
}
